/**
 * 「{order}作成」/orders/new（#233、docs/93 §4.2）：{vendor}と件名だけ聞く（{project}は任意・あとから付け替え）。?group=・?partner= で始められる。
 * {vendorQuote}から作るときは {vendorQuote}の詳細の「{order}作成」（回答の行を写す）＝ここを通らない
 */
import { ORDER_MSG, type GroupList, type Order, type Partner } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { PartnerField } from "../../components/PartnerPickerDrawer.jsx";
import { ErrorText, Field } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";

export function OrderNewPage() {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [vendorId, setVendorId] = useState(sp.get("partner") ?? "");
  /** 選んだ{vendor}（#288 の取引先検索・仕入先が既定。?partner= で入ったときは名前を引く） */
  const [picked, setPicked] = useState<string | null>(null);
  const [groupId, setGroupId] = useState(sp.get("group") ?? "");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  usePageName(t(ORDER_MSG.newTitle));
  const back = useBackNav(groupId ? { to: `/groups/${groupId}?tab=orders`, label: L.project } : { to: "/orders", label: L.order }, !!(vendorId || subject));
  const entry = useQuery({ queryKey: ["partner", sp.get("partner")], queryFn: () => api<Partner>("GET", `/partners/${sp.get("partner")}`), enabled: !!sp.get("partner") });
  const shown = picked ?? (entry.data && entry.data.id === vendorId ? entry.data.displayName : null);
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all") });
  const create = async () => {
    if (!vendorId) {
      setErr(t(ORDER_MSG.vendorRequired));
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const o = await api<Order>("POST", "/orders", { partnerId: vendorId, projectGroupId: groupId || null, subject: subject.trim() });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      nav(`/orders/${o.id}`, { replace: true });
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>{t(ORDER_MSG.newTitle)}</h1>
      </div>
      <div className="paper" style={{ maxWidth: 640, padding: 20 }} data-testid="order-new-form">
        <div style={{ marginBottom: 14 }}>
          <PartnerField
            role="vendor"
            label={L.vendor}
            value={shown ? { name: shown } : null}
            onPick={(c) => {
              setVendorId(c.id);
              setPicked(c.displayName);
              setErr(undefined);
            }}
            testId="order-new-vendor"
          />
        </div>
        <Field id="on_subject" label={ORDER_MSG.colSubject} style={{ marginBottom: 14 }}>
          <input id="on_subject" className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="order-new-subject" />
        </Field>
        <Field id="on_group" label={`${L.project}（任意）`} style={{ marginBottom: 14 }}>
          <select id="on_group" className="inp" value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="order-new-group">
            <option value="">—</option>
            {(groups.data?.items ?? []).map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </Field>
        <ErrorText>{err}</ErrorText>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn primary" disabled={busy || !vendorId} onClick={() => void create()} data-testid="order-new-create">
            {t(ORDER_MSG.create)}
          </button>
        </div>
      </div>
    </>
  );
}
